import { createTheme, alpha } from '@mui/material/styles';

// Breakfast Design System Colors (ported from Flutter)
export const BreakfastColors = {
  // Primary colors — aligned with CB Studio web flow (Figma Breakfast tokens)
  primary: '#1D7A7A',
  primaryFill: alpha('#1D7A7A', 0.1),
  
  // Background colors
  background: '#FFFFFF',
  defaultFill: '#FFFFFF',
  
  // Text colors
  title: '#010B2E',
  subtitle: '#4D546D',
  
  // Status colors
  success: '#34C759',
  warning: '#FF9500',
  notify: '#FF9500',
  notifyFill: '#1AFF9500', // 0x1AFF9500 from Flutter (exact hex)
  accent: '#5856D6',
  
  // Error colors
  error: '#FF3B30',
  errorContainer: alpha('#FF3B30', 0.1),
};

// Breakfast Design System Constants
export const BreakfastConstants = {
  cornerRadius: 17,
  defaultPadding: 20,
  spacing: 16,
  cardPadding: 16,
};

// Typography configuration
const BreakfastTypography = {
  fontFamily: '"Circular LL VIP", "CircularXX", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  
  // Headers
  headerSmall: {
    fontSize: '24px',
    fontWeight: 700,
    fontFamily: '"CircularXX", sans-serif',
    color: BreakfastColors.title,
    letterSpacing: '-0.5px',
    lineHeight: 1.2,
  },
  
  // Subheads
  subheadBold: {
    fontSize: '16px',
    fontWeight: 700,
    fontFamily: '"CircularXX", sans-serif',
    color: BreakfastColors.title,
    letterSpacing: '-0.2px',
    lineHeight: 1.3,
  },
  
  subhead: {
    fontSize: '16px',
    fontWeight: 400,
    fontFamily: '"CircularXX", sans-serif',
    color: BreakfastColors.title,
    letterSpacing: '-0.2px',
    lineHeight: 1.3,
  },
  
  // Body text
  bodySmallBold: {
    fontSize: '14px',
    fontWeight: 700,
    fontFamily: '"CircularXX", sans-serif',
    color: BreakfastColors.title,
    letterSpacing: '-0.1px',
    lineHeight: 1.4,
  },
  
  bodySmallRegular: {
    fontSize: '14px',
    fontWeight: 400,
    fontFamily: '"CircularXX", sans-serif',
    color: BreakfastColors.subtitle,
    letterSpacing: '-0.1px',
    lineHeight: 1.4,
  },
  
  // Micro text
  microtext: {
    fontSize: '12px',
    fontWeight: 400,
    fontFamily: '"CircularXX", sans-serif',
    color: BreakfastColors.subtitle,
    letterSpacing: '-0.1px',
    lineHeight: 1.4,
  },
};

// Create Material-UI theme with Breakfast Design System
export const breakfastTheme = createTheme({
  palette: {
    primary: {
      main: '#1D7A7A',
      light: BreakfastColors.primaryFill,
    },
    secondary: {
      main: BreakfastColors.accent,
    },
    error: {
      main: BreakfastColors.error,
      light: BreakfastColors.errorContainer,
    },
    warning: {
      main: BreakfastColors.notify,
      light: BreakfastColors.notifyFill,
    },
    success: {
      main: BreakfastColors.success,
    },
    background: {
      default: BreakfastColors.background,
      paper: BreakfastColors.defaultFill,
    },
    text: {
      primary: BreakfastColors.title,
      secondary: BreakfastColors.subtitle,
    },
  },
  typography: {
    fontFamily: BreakfastTypography.fontFamily,
    h1: BreakfastTypography.headerSmall,
    h2: BreakfastTypography.subheadBold,
    h3: BreakfastTypography.subhead,
    body1: BreakfastTypography.bodySmallRegular,
    body2: BreakfastTypography.bodySmallBold,
    caption: BreakfastTypography.microtext,
  },
  shape: {
    borderRadius: BreakfastConstants.cornerRadius,
  },
  spacing: 8, // Default MUI spacing unit
  components: {
    MuiCssBaseline: {
      styleOverrides: `
        @font-face {
          font-family: 'Circular LL VIP';
          font-style: normal;
          font-weight: 100 900;
          src: url('/fonts/CircularLLVIP-Upright.ttf') format('truetype');
        }
        @font-face {
          font-family: 'CircularXX';
          font-style: normal;
          font-weight: 100 900;
          src: url('/fonts/CircularLLVIP-Upright.ttf') format('truetype');
        }
        @font-face {
          font-family: 'VC Garamond Condensed';
          font-style: normal;
          font-weight: 100 900;
          src: url('/fonts/VCGaramondCondensedVF.ttf') format('truetype');
        }
        body {
          background-color: ${BreakfastColors.background};
          font-family: ${BreakfastTypography.fontFamily};
        }
      `,
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: BreakfastConstants.cornerRadius,
          textTransform: 'none',
          fontWeight: 700,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: BreakfastConstants.cornerRadius,
          boxShadow: 'none',
          border: 'none',
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 3,
          height: 6,
          backgroundColor: BreakfastColors.defaultFill,
        },
        bar: {
          borderRadius: 3,
        },
      },
    },
  },
});

const breakfastThemeExports = {
  BreakfastColors,
  BreakfastConstants,
  BreakfastTypography,
  breakfastTheme,
};

export default breakfastThemeExports;
